import { MessageTypeEnum, RecentChatMessagesQuery, RoomTypeEnum } from '@/types/generated/graphql';
import { MemberChatPreview, SidebarMemberRow } from '@modules/members/members.types';

type RecentChat = NonNullable<NonNullable<RecentChatMessagesQuery['recentChatMessages']>['data']>[number];

export function toSidebarRows(rows: (RecentChat | null)[] | null | undefined, selfWorkspaceMemberId?: number | null) {
  const members: SidebarMemberRow[] = [];
  let allMembers: SidebarMemberRow | undefined;

  for (const row of rows ?? []) {
    if (!row) continue;

    if (row.room?.type === RoomTypeEnum.group) {
      allMembers = toRow(row, selfWorkspaceMemberId, true);
      continue;
    }

    if (row.member?.id) members.push(toRow(row, selfWorkspaceMemberId));
  }

  return { members, allMembers };
}

function toRow(row: NonNullable<RecentChat>, selfWorkspaceMemberId?: number | null, isGroup = false): SidebarMemberRow {
  return {
    id: row.member?.id,
    unreadCounter: row.unreadCount,
    user: { name: row.member?.name, profilePicture: { url: row.member?.profilePictureUrl } },
    roles: [{ label: row.member?.role }],
    userOnlineManagement: { isActive: row.member?.isActive },
    isSelf: !!selfWorkspaceMemberId && row.member?.id === selfWorkspaceMemberId,
    chat: toPreview(row, selfWorkspaceMemberId, isGroup),
  };
}

function toPreview(
  row: NonNullable<RecentChat>,
  selfWorkspaceMemberId?: number | null,
  isGroup = false
): MemberChatPreview | undefined {
  const lastMessage = row.lastMessage;

  if (!lastMessage) return undefined;

  return {
    text: lastMessage.isDeleted ? undefined : lastMessage.body,
    at: row.lastMessageAt ?? lastMessage.createdAt,
    isSelfSender: !!selfWorkspaceMemberId && lastMessage.senderId === selfWorkspaceMemberId,
    senderName: isGroup ? lastMessage.senderName : undefined,
    isDeleted: !!lastMessage.isDeleted,
    isForwarded: lastMessage.type === MessageTypeEnum.forwarded,
    media: lastMessage.isDeleted ? undefined : toMediaKind(lastMessage.attachmentCount, lastMessage.attachmentMimetype),
  };
}

function toMediaKind(count?: number | null, mimetype?: string | null): MemberChatPreview['media'] {
  if (!count) return undefined;
  if (mimetype?.startsWith('audio/')) return 'voice';
  if (mimetype?.startsWith('image/')) return 'photo';
  if (mimetype?.startsWith('video/')) return 'video';

  return 'file';
}
